import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
} from "@mui/material";
import {
  useGetCountriesQuery,
  useCreateCountryMutation,
  useUpdateCountryMutation,
  useDeleteCountryMutation,
  Country,
} from "../../../entities/admin-country";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

export const CountriesManagement = () => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  // Dialog states
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Form states
  const [countryName, setCountryName] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phoneMask, setPhoneMask] = useState("");

  // Queries and mutations
  const { data: countriesData, isLoading: isLoadingCountries } =
    useGetCountriesQuery();

  const [createCountry, { isLoading: isCreating }] = useCreateCountryMutation();
  const [updateCountry, { isLoading: isUpdating }] = useUpdateCountryMutation();
  const [deleteCountry, { isLoading: isDeleting }] = useDeleteCountryMutation();

  // Handlers
  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
  };

  const handleOpenCreateDialog = () => {
    setCountryName("");
    setPhoneCode("");
    setPhoneMask("");
    setOpenCreateDialog(true);
  };

  const handleOpenEditDialog = (country: Country) => {
    setSelectedCountry(country);
    setCountryName(country.name);
    setPhoneCode(country.phoneCode);
    setPhoneMask(country.phoneMask);
    setOpenEditDialog(true);
  };

  const handleOpenDeleteDialog = (country: Country) => {
    setSelectedCountry(country);
    setOpenDeleteDialog(true);
  };

  const handleCloseDialogs = () => {
    setOpenCreateDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
  };

  const isFormValid = () => {
    return (
      countryName.trim() !== "" &&
      phoneCode.trim() !== "" &&
      phoneMask.trim() !== ""
    );
  };

  const handleCreateCountry = async () => {
    if (!isFormValid()) return;

    try {
      await createCountry({
        name: countryName.trim(),
        phoneCode: phoneCode.trim(),
        phoneMask: phoneMask.trim(),
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error creating country:", error);
    }
  };

  const handleUpdateCountry = async () => {
    if (!selectedCountry || !isFormValid()) return;

    try {
      await updateCountry({
        id: selectedCountry.id,
        body: {
          name: countryName.trim(),
          phoneCode: phoneCode.trim(),
          phoneMask: phoneMask.trim(),
        },
      }).unwrap();
      handleCloseDialogs();
    } catch (error) {
      console.error("Error updating country:", error);
    }
  };

  const handleDeleteCountry = async () => {
    if (!selectedCountry) return;

    try {
      await deleteCountry(selectedCountry.id).unwrap();
      setSelectedCountry(null);
      handleCloseDialogs();
    } catch (error) {
      console.error("Error deleting country:", error);
    }
  };

  useEffect(() => {
    if (countriesData?.countries?.length && !selectedCountry) {
      setSelectedCountry(countriesData.countries[0]);
    }
  }, [countriesData, selectedCountry]);

  // Render form fields
  const renderFormFields = () => (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField
          label="Название страны"
          fullWidth
          value={countryName}
          onChange={(e) => setCountryName(e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField
          label="Телефонный код"
          fullWidth
          value={phoneCode}
          onChange={(e) => setPhoneCode(e.target.value)}
          placeholder="+7"
        />
      </Grid>
      <Grid size={{ xs: 6 }}>
        <TextField
          label="Маска телефона"
          fullWidth
          value={phoneMask}
          onChange={(e) => setPhoneMask(e.target.value)}
          placeholder="(###) ###-##-##"
        />
      </Grid>
    </Grid>
  );

  // Render dialogs
  const renderCreateDialog = () => (
    <Dialog
      open={openCreateDialog}
      onClose={handleCloseDialogs}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Добавить страну</DialogTitle>
      <DialogContent>{renderFormFields()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleCreateCountry}
          disabled={!isFormValid() || isCreating}
        >
          {isCreating ? <CircularProgress size={24} /> : "Создать"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderEditDialog = () => (
    <Dialog
      open={openEditDialog}
      onClose={handleCloseDialogs}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Редактировать страну</DialogTitle>
      <DialogContent>{renderFormFields()}</DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleUpdateCountry}
          disabled={!isFormValid() || isUpdating}
        >
          {isUpdating ? <CircularProgress size={24} /> : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteDialog = () => (
    <Dialog open={openDeleteDialog} onClose={handleCloseDialogs}>
      <DialogTitle>Удалить страну</DialogTitle>
      <DialogContent>
        Вы уверены, что хотите удалить страну "{selectedCountry?.name}"?
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialogs}>Отмена</Button>
        <Button
          onClick={handleDeleteCountry}
          color="error"
          disabled={isDeleting}
        >
          {isDeleting ? <CircularProgress size={24} /> : "Удалить"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render selected country details
  const renderCountryDetails = () => {
    if (!selectedCountry) return null;

    return (
      <Box sx={{ mt: 2, p: 3, bgcolor: "#333333", borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Детали страны
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Название
            </Typography>
            <Typography>{selectedCountry.name}</Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Телефонный код
            </Typography>
            <Typography>{selectedCountry.phoneCode}</Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Маска телефона
            </Typography>
            <Typography>{selectedCountry.phoneMask}</Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight={600} fontSize={24} mb={2}>
        Управление странами
      </Typography>

      <Box sx={{ display: "flex" }}>
        <Paper sx={{ width: "100%", p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography gutterBottom fontWeight={600} fontSize={18}>
              Страны
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Добавить
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <Box sx={{ width: { xs: "100%", md: 300 } }}>
              {isLoadingCountries ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <List>
                  {countriesData?.countries?.map((country) => (
                    <ListItem
                      key={country.id}
                      sx={{
                        borderRadius: 1,
                        backgroundColor:
                          selectedCountry?.id === country.id
                            ? "#515151"
                            : "inherit",
                        "&:hover": {
                          cursor: "pointer",
                          backgroundColor: "#414141",
                        },
                        mb: 1,
                      }}
                      onClick={() => handleSelectCountry(country)}
                      secondaryAction={
                        <Box>
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEditDialog(country);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDeleteDialog(country);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={country.name}
                        secondary={country.phoneCode}
                        slotProps={{ primary: { fontSize: 14 } }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Box sx={{ flex: 1 }}>{renderCountryDetails()}</Box>
          </Box>
        </Paper>
      </Box>

      {renderCreateDialog()}
      {renderEditDialog()}
      {renderDeleteDialog()}
    </Box>
  );
};
